import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { Button, Card, Input } from '../../components/ui';
import type { Band } from '../../types/database';
import styles from './BandSelector.module.css';

interface Props {
  onBandLinked: () => void;
}

export function BandSelector({ onBandLinked }: Props) {
  const { user } = useAuth();
  const [bands, setBands] = useState<Band[]>([]);
  const [selectedBandId, setSelectedBandId] = useState<string | null>(null);
  const [accessCode, setAccessCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [linking, setLinking] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchBands = async () => {
      const { data } = await supabase
        .from('bands')
        .select('id, name, logo_url')
        .eq('is_active', true)
        .order('name');

      if (data) {
        setBands(data as Band[]);
      }
      setLoading(false);
    };

    fetchBands();
  }, []);

  // Shared test access code for all bands during testing
  const TEST_ACCESS_CODE = 'masmatch2026';

  const handleLinkBand = async () => {
    if (!user || !selectedBandId) return;

    setLinking(true);
    setError('');

    // Validate access code
    if (!accessCode.trim()) {
      setError('Access code is required');
      setLinking(false);
      return;
    }

    if (accessCode.trim().toLowerCase() !== TEST_ACCESS_CODE.toLowerCase()) {
      setError('Invalid access code. Contact the band administrator.');
      setLinking(false);
      return;
    }

    const { error: linkError } = await supabase.from('band_managers').insert({
      user_id: user.id,
      band_id: selectedBandId,
      role: 'admin',
    });

    if (linkError) {
      if (linkError.code === '23505') {
        setError('You are already linked to this band');
      } else {
        setError('Failed to link band. Please try again.');
      }
      setLinking(false);
      return;
    }

    onBandLinked();
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.spinner} />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.header}>
          <h1>Band Manager Access</h1>
          <p>Select your band to access the manager dashboard</p>
        </div>

        <Card className={styles.card}>
          <div className={styles.bandList}>
            {bands.map((band) => (
              <button
                key={band.id}
                className={`${styles.bandOption} ${selectedBandId === band.id ? styles.selected : ''}`}
                onClick={() => setSelectedBandId(band.id)}
              >
                <div className={styles.bandLogo}>
                  {band.logo_url ? (
                    <img src={band.logo_url} alt={band.name} />
                  ) : (
                    <span>{band.name[0]}</span>
                  )}
                </div>
                <span>{band.name}</span>
                {selectedBandId === band.id && (
                  <svg
                    className={styles.checkmark}
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                  </svg>
                )}
              </button>
            ))}
          </div>

          <div className={styles.accessCode}>
            <Input
              label="Access Code"
              value={accessCode}
              onChange={(e) => setAccessCode(e.target.value)}
              placeholder="Enter band access code"
              required
            />
            <p className={styles.hint}>
              Contact the band administrator for the access code
            </p>
          </div>

          {error && <p className={styles.error}>{error}</p>}

          <Button
            fullWidth
            onClick={handleLinkBand}
            disabled={!selectedBandId || !accessCode.trim()}
            loading={linking}
          >
            Access Dashboard
          </Button>
        </Card>
      </div>
    </div>
  );
}
